/**
 * EngagementList — displays post engagements with intent classification.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Heart, MessageCircle, Share2, ThumbsUp, Users, ExternalLink, MessageSquareReply } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { useApi } from '@/hooks/use-api'

type Engagement = {
  readonly id: string
  readonly engager_name: string
  readonly engager_headline?: string
  readonly engager_linkedin_url?: string
  readonly engagement_type: string
  readonly comment_text?: string
  readonly intent_classification?: string
  readonly dm_sent: boolean
  /** Comment-to-DM pipeline state for this engagement. */
  readonly dm_status?: string
  readonly lead_id?: string
  readonly created_at: string
}

type EngagementResponse = {
  readonly engagements: readonly Engagement[]
  readonly total: number
  readonly comment_dm?: { readonly plan_allows: boolean; readonly enabled: boolean }
}

/** dm_status (post_engagements, 0032) to a short label. */
const DM_STATUS_LABELS: Record<string, string> = {
  sent: 'DM sent',
  held: 'DM waiting for approval',
  rejected: 'DM discarded',
  skipped_not_connected: 'Not connected, no DM',
  skipped_duplicate: 'Already messaged',
  failed: 'DM failed',
}

/**
 * Comment-to-DM status, shown on the Engagement tab so the toggle is easy
 * to find from where the engagement actually shows up.
 */
function CommentDmBanner({ state }: { readonly state?: EngagementResponse['comment_dm'] }) {
  if (!state) return null
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="flex items-start gap-2">
        <MessageSquareReply className="mt-0.5 h-4 w-4 text-cobalt-bright" />
        <div className="text-xs">
          <p className="text-sm font-medium">
            Comment-to-DM is {state.plan_allows && state.enabled ? 'on' : 'off'}
          </p>
          <p className="text-muted-foreground">
            {state.plan_allows
              ? 'When on, people who engage with your posts get a short DM if they are 1st-degree connections (up to 10 a day, weekdays 9 AM to 5 PM New York time). Add a comment keyword to a post to send your own message to everyone who comments it.'
              : 'Messaging people who engage with your posts is part of the Pro plan.'}
          </p>
        </div>
      </div>
      {state.plan_allows && (
        <Link to="/settings?tab=safety" className="text-xs font-medium text-cobalt-bright hover:underline">
          {state.enabled ? 'Change in Settings' : 'Turn on in Settings'}
        </Link>
      )}
    </div>
  )
}

const TYPE_ICONS: Record<string, typeof Heart> = {
  like: Heart,
  comment: MessageCircle,
  share: Share2,
  reaction: ThumbsUp,
}

const INTENT_COLORS: Record<string, string> = {
  interested: 'bg-emerald-500/12 text-emerald-300',
  curious: 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)]',
  agreement: 'bg-emerald-500/12 text-emerald-700',
  promotional: 'bg-orange-100 text-orange-700',
  negative: 'bg-red-500/12 text-red-300',
  generic: 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)]',
}

export function EngagementList() {
  const apiFetch = useApi()

  const { data, isLoading } = useQuery({
    queryKey: ['content', 'engagements'],
    queryFn: async (): Promise<EngagementResponse> => {
      const res = await apiFetch<EngagementResponse>('/api/content/engagement')
      if (!res.success || !res.data) return { engagements: [], total: 0 }
      return res.data
    },
    staleTime: 60_000,
  })

  const engagements = data?.engagements ?? []
  const banner = <CommentDmBanner state={data?.comment_dm} />

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Loading engagements...
        </CardContent>
      </Card>
    )
  }

  if (engagements.length === 0) {
    return (
      <div className="space-y-3">
      {banner}
      <Card>
        <CardContent className="py-12 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No engagements yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Engagements from your published posts will appear here. They are collected every hour for posts published in the last 30 days.
          </p>
        </CardContent>
      </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {banner}
      {engagements.map((eng) => {
        const Icon = TYPE_ICONS[eng.engagement_type] ?? ThumbsUp
        const intentColor = eng.intent_classification && eng.intent_classification !== 'keyword_match'
          ? INTENT_COLORS[eng.intent_classification] ?? INTENT_COLORS.generic
          : null

        const isLead = Boolean(eng.lead_id)
        return (
          <Card
            key={eng.id}
            className={isLead ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/20' : ''}
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`mt-0.5 rounded-full p-1.5 ${isLead ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Icon className={`h-3.5 w-3.5 ${isLead ? 'text-cobalt-bright' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{eng.engager_name}</p>
                      {isLead && (
                        <Badge className="text-[10px] bg-primary text-primary-foreground border-0 gap-1 shrink-0">
                          <Users className="h-3 w-3" />
                          Lead
                        </Badge>
                      )}
                      {eng.engager_linkedin_url && (
                        <a href={eng.engager_linkedin_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                          <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                        </a>
                      )}
                    </div>
                    {eng.engager_headline && (
                      <p className="text-xs text-muted-foreground truncate">{eng.engager_headline}</p>
                    )}
                    {eng.comment_text && (
                      <p className="mt-1 text-sm text-secondary-foreground line-clamp-2">{eng.comment_text}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {eng.intent_classification && intentColor && (
                    <Badge className={`text-[10px] ${intentColor} border-0`}>
                      {eng.intent_classification}
                    </Badge>
                  )}
                  {eng.intent_classification === 'keyword_match' && (
                    <Badge variant="outline" className="text-[10px]">Keyword</Badge>
                  )}
                  {(eng.dm_status || eng.dm_sent) && (
                    <Badge variant="outline" className="text-[10px]">
                      {DM_STATUS_LABELS[eng.dm_status ?? 'sent'] ?? 'DM sent'}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
